/**
 * A portal's calculator handed a calculation to Tonnie, and the person is on a
 * phone — where Citizen Web does not run (ViewportGuard). The calculation goes
 * to the app instead (D1-B):
 *
 *   - "Open in the app" hands the token to the installed app
 *     (`tonniecitizen://inout/handoff?token=…`), which keeps it, has the
 *     person sign up or in, and makes it their InOut;
 *   - "Install the app" first copies the app's link for the token (a fresh
 *     `/l/inout/handoff` link) — the app picks it up on its first launch
 *     (`useDeferredDeepLink`) — then goes to the store;
 *   - the heading says the calculation back, as sign-up does; a link used or
 *     past its 72 hours is said as such, and the app's calculator remains.
 *
 * The token leaves the address at once, as on sign-up.
 */
import { Smartphone } from '@tonnie/assets';
import { inoutHandoffDescription, inoutHandoffTitle } from '@tonnie/heroes';
import { useCountryName, useLocale } from '@tonnie/locale';
import { isInOutHandoffGone, useInOutHandoffSummary } from '@tonnie/sdk';
import { Button, buttonVariants, cn } from '@tonnie/ui';
import { INOUT_CLASSES } from '@tonnie/inout-ui';
import { useState } from 'react';

import {
  buildCustomSchemeLink,
  buildDeepLink,
  DeepLinkActions,
  getStoreUrl,
} from '@/shared/deep-links';

import { pendingHandoff, takeHandoffFromAddress } from '../lib/pending-handoff';

export function InOutHandoffOnPhone() {
  const { t, formatNumber } = useLocale();
  const countryName = useCountryName();
  const [token] = useState(() => takeHandoffFromAddress() ?? pendingHandoff());
  const summary = useInOutHandoffSummary(token);
  const gone = token === null || (summary.isError && isInOutHandoffGone(summary.error));

  const title = gone
    ? t('inout.signup.goneTitle')
    : summary.data
      ? inoutHandoffTitle(summary.data.portal, t)
      : t('inout.phone.title');
  const description = gone
    ? t('inout.phone.goneBody')
    : summary.data
      ? inoutHandoffDescription(summary.data.summary, { t, formatNumber, countryName })
      : t('inout.signup.unreadDescription');

  const install = async () => {
    if (token) {
      try {
        // The app reads it on its first launch, within its freshness window.
        await navigator.clipboard.writeText(
          buildDeepLink(DeepLinkActions.INOUT_HANDOFF, { token, timestamp: String(Date.now()) })
        );
      } catch {
        // No clipboard: the app's own calculator remains after installing.
      }
    }
    window.location.assign(getStoreUrl());
  };

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-background p-6"
      data-testid="inout-handoff-on-phone"
    >
      <div className="w-full max-w-md space-y-6 text-center">
        <div
          className={cn(
            'inline-flex h-16 w-16 items-center justify-center rounded-full',
            INOUT_CLASSES.subtle
          )}
        >
          <Smartphone className={cn('h-8 w-8', INOUT_CLASSES.text)} aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground text-balance">{title}</h1>
          <p className="text-muted-foreground">{description}</p>
          {gone ? null : <p className="text-sm text-muted-foreground">{t('inout.phone.body')}</p>}
        </div>
        <div className="flex flex-col gap-3">
          {token && !gone ? (
            <a
              href={buildCustomSchemeLink(DeepLinkActions.INOUT_HANDOFF, { token })}
              className={cn(buttonVariants({ size: 'lg' }), 'w-full')}
              data-testid="inout-handoff-open-app"
            >
              {t('inout.phone.openApp')}
            </a>
          ) : null}
          <Button
            type="button"
            size="lg"
            variant={token && !gone ? 'outline' : 'default'}
            className="w-full"
            onClick={() => void install()}
            data-testid="inout-handoff-install-app"
          >
            {t('inout.phone.installApp')}
          </Button>
          {token && !gone ? (
            <p className="text-xs text-muted-foreground">{t('inout.phone.installHint')}</p>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">{t('viewport.orAccessDesktop')}</p>
      </div>
    </main>
  );
}
